import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import NotificationSystem from '../components/NotificationSystem';
import { useNotification } from '../hooks/useNotification';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  PlayArrow as TestIcon,
  IntegrationInstructions as IntegrationIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

/**
 * Sistema de Cache de Logos:
 * - As logos são carregadas da API apenas uma vez
 * - Armazenadas no localStorage por 24 horas
 * - Para forçar recarregamento: clearLogosCache() no console
 */
const AppConfig = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para modais
  const [openCredentialsModal, setOpenCredentialsModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  
  const [credentials, setCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [logos, setLogos] = useState({});
  
  // Sistema de notificações padronizado
  const { notification, showSuccess, showError } = useNotification();

  // Carregar aplicações
  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Token de autenticação não encontrado. Faça login novamente.');
        return;
      }
      
      const response = await fetch('/api/app-config/applications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data.data);
      } else {
        const errorData = await response.json();
        console.error('Erro na API:', errorData);
        setError(errorData.error || 'Erro ao carregar aplicações');
        showError('Erro ao carregar aplicações');
      }
    } catch (error) {
      console.error('Erro ao carregar aplicações:', error);
      setError('Erro de conexão ao carregar aplicações');
      showError('Erro de conexão ao carregar aplicações');
    } finally {
      setLoading(false);
    }
  };

  // Limpar cache de logos
  const clearLogosCache = () => {
    localStorage.removeItem('appLogos');
    localStorage.removeItem('appLogosTimestamp');
    console.log('Cache de logos limpo');
    // Recarregar logos após limpar cache
    loadLogos();
  };

  // Carregar templates
  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.error('Token não encontrado para carregar templates');
        return;
      }
      
      const response = await fetch('/api/app-config/templates', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.data);
      } else {
        console.error('Erro ao carregar templates:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

    // Carregar logos das aplicações
  const loadLogos = async () => {
    try {
      // Verificar se já temos logos no localStorage
      const cachedLogos = localStorage.getItem('appLogos');
      if (cachedLogos) {
        try {
          const parsedLogos = JSON.parse(cachedLogos);
          const cacheTimestamp = localStorage.getItem('appLogosTimestamp');
          const now = Date.now();
          
          // Cache válido por 24 horas
          if (cacheTimestamp && (now - parseInt(cacheTimestamp)) < 24 * 60 * 60 * 1000) {
            console.log('Usando logos do cache');
            setLogos(parsedLogos);
            return;
          }
        } catch (error) {
          console.error('Erro ao parsear logos do cache:', error);
        }
      }
      
      console.log('Carregando logos da API...');
      const logoQueries = {
        'gmail': 'google',
        'zoho_desk': 'zoho'
      };

      const newLogos = {};
      
      for (const [type, query] of Object.entries(logoQueries)) {
        try {
          console.log(`Buscando logo para ${type} com query: ${query}`);
          const response = await fetch(`https://api.logo.dev/search?q=${encodeURIComponent(query)}`, {
            headers: {
              'Authorization': 'Bearer sk_AgqCkwrMTt6OKQXIExi4pA'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log(`Resposta para ${type}:`, data);
            if (data && data.length > 0) {
              newLogos[type] = data[0].logo_url;
              console.log(`Logo encontrada para ${type}:`, data[0].logo_url);
            }
          } else {
            console.error(`Erro na resposta para ${type}:`, response.status);
          }
        } catch (error) {
          console.error(`Erro ao carregar logo para ${type}:`, error);
        }
      }
      
      // Salvar no localStorage
      localStorage.setItem('appLogos', JSON.stringify(newLogos));
      localStorage.setItem('appLogosTimestamp', Date.now().toString());
      
      console.log('Logos carregadas e salvas no cache:', newLogos);
      setLogos(newLogos);
    } catch (error) {
      console.error('Erro ao carregar logos:', error);
    }
  };

  useEffect(() => {
    loadApplications();
    loadTemplates();
    loadLogos();
  }, []);



  // Configurar credenciais
  const handleSaveCredentials = async () => {
    try {
      setFormLoading(true);
      
      // Mapear nomes dos campos da API para os nomes esperados pelo backend
      const fieldMapping = {
        'clientId': 'client_id',
        'clientSecret': 'client_secret',
        'redirectUri': 'redirect_uri',
        'refreshToken': 'refresh_token',
        'organizationId': 'organization_id'
      };
      
      // Filtrar apenas campos que foram realmente preenchidos
      const credentialsToSave = {};
      Object.keys(credentials).forEach(key => {
        if (credentials[key] && credentials[key].trim() !== '') {
          // Se o valor contém asteriscos no meio (formato mascarado), não enviar
          if (!credentials[key].includes('****')) {
            // Usar apenas o nome mapeado para o backend
            const backendFieldName = fieldMapping[key];
            if (backendFieldName) {
              credentialsToSave[backendFieldName] = credentials[key];
            }
          }
        }
      });
      
      console.log('Credenciais para salvar:', credentialsToSave);
      
      // Se não há campos para salvar, mostrar erro
      if (Object.keys(credentialsToSave).length === 0) {
        showError('Preencha pelo menos um campo para salvar as credenciais');
        setFormLoading(false);
        return;
      }
      
      const response = await fetch(`/api/app-config/applications/${selectedApp.id}/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(credentialsToSave)
      });
      
      if (response.ok) {
        showSuccess('Credenciais configuradas com sucesso!');
        setOpenCredentialsModal(false);
        setCredentials({});
        setSelectedApp(null);
        loadApplications();
      } else {
        const data = await response.json();
        showError(data.error || 'Erro ao configurar credenciais');
      }
    } catch (error) {
      showError('Erro ao configurar credenciais');
    } finally {
      setFormLoading(false);
    }
  };

  // Testar aplicação
  const handleTestApp = async (appId) => {
    try {
      console.log('Testando aplicação:', appId);
      const token = localStorage.getItem('authToken');
      console.log('Token:', token ? 'Presente' : 'Ausente');
      
      const response = await fetch(`/api/app-config/applications/${appId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Teste bem-sucedido:', data);
        const message = data.message || 'Teste realizado com sucesso!';
        console.log('Mostrando sucesso:', message);
        showSuccess(message);
        console.log('Estado da notificação após showSuccess:', notification);
      } else {
        const data = await response.json();
        console.error('Erro no teste:', data);
        const errorMessage = data.error || 'Erro ao testar aplicação';
        console.log('Mostrando erro:', errorMessage);
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao testar aplicação:', error);
      showError('Erro ao testar aplicação');
    }
  };



  // Remover apenas integração (dados de integração)
  const handleRemoveIntegration = async (appId) => {
    if (!window.confirm('Tem certeza que deseja remover apenas os dados de integração? A aplicação permanecerá, mas sem credenciais.')) return;
    
    try {
      const response = await fetch(`/api/app-config/applications/${appId}/credentials`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        showSuccess('Dados de integração removidos com sucesso!');
        loadApplications();
      } else {
        const data = await response.json();
        showError(data.error || 'Erro ao remover dados de integração');
      }
    } catch (error) {
      showError('Erro ao remover dados de integração');
    }
  };

  // Abrir modal de credenciais
  const openCredentials = async (app) => {
    setSelectedApp(app);
    setShowPasswords({});
    setOpenCredentialsModal(true);
    
    // Carregar credenciais existentes
    if (app.hasCredentials) {
      try {
        console.log('Carregando credenciais para app:', app.id);
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/app-config/applications/${app.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Dados recebidos:', data);
          if (data.data && data.data.credentials) {
            console.log('Credenciais encontradas:', data.data.credentials);
            setCredentials(data.data.credentials);
          } else {
            console.log('Nenhuma credencial encontrada');
            setCredentials({});
          }
        } else {
          console.log('Erro na resposta:', response.status);
          setCredentials({});
        }
      } catch (error) {
        console.error('Erro ao carregar credenciais:', error);
        setCredentials({});
      }
    } else {
      console.log('App não tem credenciais configuradas');
      setCredentials({});
    }
  };

  // Toggle visibilidade de senha
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Renderizar campos de credenciais
  const renderCredentialFields = () => {
    if (!selectedApp || !templates[selectedApp.type]) return null;
    
    const template = templates[selectedApp.type];
    
    // Mapear nomes dos campos do template para os nomes retornados pela API
    const fieldMapping = {
      'client_id': 'clientId',
      'client_secret': 'clientSecret',
      'redirect_uri': 'redirectUri',
      'refresh_token': 'refreshToken',
      'organization_id': 'organizationId'
    };
    
    return template.fields.map(field => {
      const apiFieldName = fieldMapping[field.name];
      const fieldValue = credentials[apiFieldName] || '';
      
      console.log(`Campo ${field.name} -> ${apiFieldName}: ${fieldValue}`);
      
      return (
        <TextField
          key={field.name}
          fullWidth
          margin="normal"
          label={field.label}
          type={field.type === 'password' && !showPasswords[field.name] ? 'password' : 'text'}
          value={fieldValue}
          onChange={(e) => setCredentials(prev => ({
            ...prev,
            [apiFieldName]: e.target.value
          }))}
          required={field.required}
          InputProps={{
            endAdornment: field.type === 'password' && (
              <IconButton
                onClick={() => togglePasswordVisibility(field.name)}
                edge="end"
              >
                {showPasswords[field.name] ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            )
          }}
        />
      );
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      py: 2
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Configuração de Aplicações
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gerencie as integrações e credenciais das aplicações
        </Typography>
      </Box>



      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Applications Grid */}
      <Grid container spacing={3}>
        {applications.map((app) => (
          <Grid item xs={12} sm={6} md={4} key={app.id}>
            <Card 
              sx={{ 
                height: 280, // Altura reduzida para cards mais compactos
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                border: '1px solid #e0e0e0',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              {/* Header compacto */}
              <Box sx={{ 
                p: 2, 
                background: '#f8f9fa',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 60
              }}>
                <Box sx={{ 
                  width: 28, 
                  height: 28, 
                  borderRadius: 0.5, 
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0'
                }}>
                  {logos[app.type] ? (
                    <img 
                      src={logos[app.type]} 
                      alt={`${app.name} logo`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: '2px'
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '14px' }}>
                      {app.type === 'gmail' ? '📧' : app.type === 'zoho_desk' ? '🎫' : '🔗'}
                    </span>
                  )}
                </Box>
                <Typography variant="h6" component="h2" sx={{ 
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#2c3e50'
                }}>
                  {app.name}
                </Typography>
              </Box>

                              {/* Conteúdo do Card */}
               <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                 {/* Status das Credenciais */}
                 <Box sx={{ mb: 1.5 }}>
                   <Chip
                     icon={app.hasCredentials ? <CheckCircleIcon /> : <ErrorIcon />}
                     label={app.hasCredentials ? 'Configurado' : 'Pendente'}
                     color={app.hasCredentials ? 'success' : 'warning'}
                     size="small"
                     sx={{ 
                       fontWeight: 500,
                       fontSize: '0.7rem',
                       height: 24,
                       color: 'white',
                       '& .MuiChip-label': {
                         color: 'white !important'
                       },
                       '& .MuiChip-icon': {
                         color: 'white !important'
                       }
                     }}
                   />
                 </Box>

                 {/* Informações de Data */}
                 <Box sx={{ mb: 2 }}>
                   <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                     Criado: {new Date(app.createdAt).toLocaleDateString('pt-BR')}
                   </Typography>
                   <Typography variant="caption" color="text.secondary" display="block">
                     Atualizado: {new Date(app.updatedAt).toLocaleDateString('pt-BR')}
                   </Typography>
                 </Box>

                 {/* Botões de Ação */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 'auto' }}>
                     <Button
                       size="small"
                       variant="contained"
                       startIcon={<SecurityIcon />}
                       onClick={() => openCredentials(app)}
                       sx={{ 
                         backgroundColor: '#1976d2',
                         color: 'white',
                         textTransform: 'none',
                         fontWeight: 500,
                         fontSize: '0.7rem',
                         py: 0.5,
                         px: 1.5,
                         flex: 1,
                         '&:hover': {
                           backgroundColor: '#1565c0',
                         }
                       }}
                     >
                       Credenciais
                     </Button>
                     
                     <Button
                       size="small"
                       variant="contained"
                       startIcon={<TestIcon />}
                       onClick={() => handleTestApp(app.id)}
                       sx={{ 
                         backgroundColor: '#2e7d32',
                         color: 'white',
                         textTransform: 'none',
                         fontWeight: 500,
                         fontSize: '0.7rem',
                         py: 0.5,
                         px: 1.5,
                         flex: 1,
                         '&:hover': {
                           backgroundColor: '#1b5e20',
                         }
                       }}
                     >
                       Testar
                     </Button>
                     
                     <Button
                       size="small"
                       variant="contained"
                       startIcon={<IntegrationIcon />}
                       onClick={() => handleRemoveIntegration(app.id)}
                       sx={{ 
                         backgroundColor: '#d32f2f',
                         color: 'white',
                         textTransform: 'none',
                         fontWeight: 500,
                         fontSize: '0.7rem',
                         py: 0.5,
                         px: 1.5,
                         flex: 1,
                         '&:hover': {
                           backgroundColor: '#c62828',
                         }
                       }}
                     >
                       Remover
                     </Button>
                   </Box>
               </CardContent>

                 {/* Footer removido - botões agora estão no conteúdo */}
            </Card>
          </Grid>
        ))}
      </Grid>

             {/* Empty State */}
       {applications.length === 0 && !loading && (
         <Box textAlign="center" py={8}>
           <Typography variant="h6" color="textSecondary" gutterBottom>
             Nenhuma aplicação configurada
           </Typography>
           <Typography variant="body2" color="textSecondary">
             Configure suas integrações para começar a usar o sistema
           </Typography>
         </Box>
       )}

      

             {/* Modal Credenciais */}
       <Dialog open={openCredentialsModal} onClose={() => setOpenCredentialsModal(false)} maxWidth="md" fullWidth>
         <DialogTitle>
           Configurar Credenciais - {selectedApp?.name}
         </DialogTitle>
         <DialogContent>
           <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
             {selectedApp && templates[selectedApp.type]?.description}
           </Typography>
           {selectedApp?.hasCredentials && (
             <Alert severity="info" sx={{ mb: 2 }}>
               <Typography variant="body2">
                 <strong>Credenciais já configuradas:</strong> Os campos mostram os valores atuais. 
                 Client ID e Redirect URI são exibidos completamente. Secrets mostram apenas o início e fim por segurança.
                 Para atualizar, preencha os novos valores. Para manter os valores atuais, deixe os campos como estão.
               </Typography>
             </Alert>
           )}
           {renderCredentialFields()}
         </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCredentialsModal(false)} disabled={formLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveCredentials} 
            variant="contained"
            disabled={formLoading}
          >
            {formLoading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sistema de Notificações */}
      <NotificationSystem 
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={notification.onClose}
      />
    </Box>
  );
};

export default AppConfig;
